import React, { useState, useEffect } from "react";
import Topbar, { AppMode } from "./components/Topbar";
import Login from "./components/Login";
import TreatmentPlanWizard from "./components/TreatmentPlanWizard";
import DomainsSidebar from "./components/DomainsSidebar";
import GoalsManagement from "./components/GoalsManagement";
import DecisionSupportSidebar, {
  AnalysisType,
} from "./components/DecisionSupportSidebar";
import DecisionSupportPanel from "./components/DecisionSupportPanel";
import ParentTrainingPanel from "./components/ParentTrainingPanel";
import { ModelInfo } from "./types";
import { getAvailableModels } from "./services/api";
import authService from "./services/auth";
import {
  getDomains,
  getGoals,
  getAllUsers,
  Domain,
  Goal,
  User,
} from "./services/treatmentGoals";
import * as decisionSupportService from "./services/decisionSupport";
import { ChatSession, ChatMessage } from "./services/decisionSupport";
import NewSessionModal from "./components/NewSessionModal";
import "./App.css";

const App: React.FC = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // App mode state - default to Treatment Goals
  const [appMode, setAppMode] = useState<AppMode>(AppMode.TREATMENT_GOALS);

  // Global models list
  const [models, setModels] = useState<ModelInfo[]>([]);

  // Treatment Goals state
  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
  const [domainGoals, setDomainGoals] = useState<Goal[]>([]);
  const [authors, setAuthors] = useState<User[]>([]);
  const [domainsSidebarCollapsed, setDomainsSidebarCollapsed] = useState(false);

  // Decision Support mode state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [sessionMessages, setSessionMessages] = useState<ChatMessage[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [decisionSupportSidebarCollapsed, setDecisionSupportSidebarCollapsed] =
    useState(false);
  const [streamingContent, setStreamingContent] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const user = await authService.getCurrentUser();
          if (user) {
            setCurrentUser(user);
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();

    const authCheckInterval = setInterval(async () => {
      const isAuth = authService.isAuthenticated();
      if (!isAuth && isAuthenticated) {
        console.log("Authentication lost - redirecting to login");
        setIsAuthenticated(false);
        setCurrentUser(null);
        setDomains([]);
        setDomainGoals([]);
        setAuthors([]);
        setSelectedDomainId(null);
        setSessions([]);
        setActiveSessionId(null);
        setSessionMessages([]);
      }
    }, 5000);

    return () => clearInterval(authCheckInterval);
  }, [isAuthenticated]);

  // Load models when authenticated
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    const fetchModels = async () => {
      try {
        const data = await getAvailableModels();
        setModels(data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };

    fetchModels();
  }, [isAuthenticated, currentUser?.id]);

  // Fetch domains when authenticated
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    const fetchDomains = async () => {
      try {
        const data = await getDomains();
        setDomains(data);
        if (data.length > 0 && !selectedDomainId) {
          setSelectedDomainId(data[0].id);
        }
      } catch (error) {
        console.error("Error fetching domains:", error);
      }
    };

    fetchDomains();
  }, [isAuthenticated, currentUser?.id]);

  // Fetch goals when domain changes
  useEffect(() => {
    if (!selectedDomainId || !currentUser) {
      setDomainGoals([]);
      return;
    }

    const fetchGoals = async () => {
      try {
        const goals = await getGoals(selectedDomainId);
        setDomainGoals(goals);
      } catch (error) {
        console.error("Error fetching goals:", error);
      }
    };

    fetchGoals();
  }, [selectedDomainId, currentUser?.id]);

  // Fetch all users when authenticated
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    const fetchAuthors = async () => {
      try {
        const data = await getAllUsers();
        setAuthors(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchAuthors();
  }, [isAuthenticated, currentUser?.id]);

  // Load sessions when switching to Decision Support mode
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    if (appMode !== AppMode.DECISION_SUPPORT) return;

    const loadSessions = async () => {
      setIsLoadingSessions(true);
      try {
        const sessionsList = await decisionSupportService.listSessions();
        setSessions(sessionsList);
      } catch (error) {
        console.error("Error loading sessions:", error);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadSessions();
  }, [isAuthenticated, currentUser?.id, appMode]);

  // Load messages when a session is selected
  useEffect(() => {
    if (!activeSessionId) {
      setSessionMessages([]);
      return;
    }

    const loadMessages = async () => {
      setIsLoadingMessages(true);
      try {
        const messages = await decisionSupportService.getSessionMessages(
          activeSessionId
        );
        setSessionMessages(messages);
      } catch (error) {
        console.error("Error loading messages:", error);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadMessages();
  }, [activeSessionId]);

  const refreshDomains = async () => {
    try {
      const data = await getDomains();
      setDomains(data);
    } catch (error) {
      console.error("Error refreshing domains:", error);
    }
  };

  const refreshGoals = async () => {
    if (!selectedDomainId) return;
    try {
      const goals = await getGoals(selectedDomainId);
      setDomainGoals(goals);
    } catch (error) {
      console.error("Error refreshing goals:", error);
    }
  };

  const refreshAuthors = async () => {
    try {
      const data = await getAllUsers();
      setAuthors(data);
    } catch (error) {
      console.error("Error refreshing users:", error);
    }
  };

  const refreshGoalsAndDomains = async () => {
    await Promise.all([refreshGoals(), refreshDomains(), refreshAuthors()]);
  };

  // Decision Support Handlers
  const handleCreateSession = async (
    title: string,
    analysisType: AnalysisType,
    startingPrompt: string,
    files: File[],
    ragEnabled: boolean,
    ragNumChunks: number,
    ragMinScore: number
  ) => {
    try {
      setStreamingContent("");
      setIsStreaming(true);

      await decisionSupportService.createSessionStream(
        title,
        analysisType,
        startingPrompt,
        files,
        ragEnabled,
        ragNumChunks,
        ragMinScore,
        (content: string) => {
          setStreamingContent((prev) => prev + content);
        },
        (sessionId: number, userMessage: ChatMessage) => {
          const newSession: ChatSession = {
            id: sessionId,
            title,
            analysis_type: analysisType,
            starting_prompt: startingPrompt,
            files_metadata: files.map((f) => ({
              filename: f.name,
              size: f.size,
              type: f.type,
            })),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_message_at: new Date().toISOString(),
            message_count: 1,
          };

          setSessions((prev) => [newSession, ...prev]);
          setActiveSessionId(sessionId);
          setSessionMessages([userMessage]);
          setShowNewSessionModal(false);
        },
        (session: ChatSession, messages: ChatMessage[]) => {
          setSessions((prev) =>
            prev.map((s) => (s.id === session.id ? session : s))
          );
          setSessionMessages(messages);
          setIsStreaming(false);
          setStreamingContent("");
        },
        (error: string) => {
          console.error("Error creating session:", error);
          setIsStreaming(false);
          setStreamingContent("");
          throw new Error(error);
        }
      );
    } catch (error) {
      console.error("Error creating session:", error);
      setIsStreaming(false);
      setStreamingContent("");
      throw error;
    }
  };

  const handleSelectSession = (sessionId: number) => {
    setActiveSessionId(sessionId);
  };

  const handleDeleteSession = async (sessionId: number) => {
    try {
      await decisionSupportService.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      alert("Failed to delete session");
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!activeSessionId) return;

    const tempUserMessage: ChatMessage = {
      id: -Date.now(),
      role: "user",
      content,
      extra_data: {},
      created_at: new Date().toISOString(),
    };
    setSessionMessages((prev) => [...prev, tempUserMessage]);

    setStreamingContent("");
    setIsStreaming(true);
    setIsSendingMessage(true);

    try {
      await decisionSupportService.sendMessageStream(
        activeSessionId,
        content,
        (streamContent: string) => {
          setStreamingContent((prev) => prev + streamContent);
        },
        (userMessage: ChatMessage, assistantMessage: ChatMessage) => {
          setSessionMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== tempUserMessage.id);
            return [...withoutTemp, userMessage, assistantMessage];
          });

          setSessions((prev) =>
            prev.map((s) =>
              s.id === activeSessionId
                ? {
                    ...s,
                    last_message_at: new Date().toISOString(),
                    message_count: s.message_count + 2,
                  }
                : s
            )
          );

          setIsStreaming(false);
          setStreamingContent("");
        },
        (error: string) => {
          console.error("Error sending message:", error);
          setSessionMessages((prev) =>
            prev.filter((m) => m.id !== tempUserMessage.id)
          );
          setIsStreaming(false);
          setStreamingContent("");
        }
      );
    } catch (error) {
      console.error("Error sending message:", error);
      setSessionMessages((prev) =>
        prev.filter((m) => m.id !== tempUserMessage.id)
      );
      setIsStreaming(false);
      setStreamingContent("");
      throw error;
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    authService.getCurrentUser().then((user) => {
      setCurrentUser(user);
    });
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setDomains([]);
    setDomainGoals([]);
    setAuthors([]);
    setSelectedDomainId(null);
    setSessions([]);
    setActiveSessionId(null);
    setSessionMessages([]);
  };

  // Enforce role-based default mode after login
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      if (!currentUser.is_superuser && appMode !== AppMode.TREATMENT_GOALS) {
        setAppMode(AppMode.TREATMENT_GOALS);
      }
    }
  }, [isAuthenticated, currentUser, appMode]);

  const handleModeChange = (mode: AppMode) => {
    if (currentUser?.is_superuser) {
      setAppMode(mode);
    } else {
      if (mode === AppMode.TREATMENT_GOALS) {
        setAppMode(mode);
      }
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="app-loading-overlay">
        <div className="app-loading-content">
          <div className="spinner"></div>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      <Topbar
        activeMode={appMode}
        onModeChange={handleModeChange}
        currentUser={currentUser}
        onLogout={handleLogout}
      />

      <div className="app-body">
        {!currentUser?.is_superuser || appMode === AppMode.TREATMENT_GOALS ? (
          <DomainsSidebar
            domains={domains}
            activeDomainId={selectedDomainId}
            onDomainSelect={setSelectedDomainId}
            onAddDomain={() => {
              console.log("Add domain");
            }}
            isCollapsed={domainsSidebarCollapsed}
            onToggleCollapse={() =>
              setDomainsSidebarCollapsed(!domainsSidebarCollapsed)
            }
          />
        ) : currentUser?.is_superuser &&
          appMode === AppMode.TREATMENT_PLAN ? (
          null
        ) : currentUser?.is_superuser &&
          appMode === AppMode.DECISION_SUPPORT ? (
          <DecisionSupportSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSessionSelect={handleSelectSession}
            onNewSession={() => setShowNewSessionModal(true)}
            onDeleteSession={handleDeleteSession}
            isCollapsed={decisionSupportSidebarCollapsed}
            onToggleCollapse={() =>
              setDecisionSupportSidebarCollapsed(
                !decisionSupportSidebarCollapsed
              )
            }
            isLoading={isLoadingSessions}
          />
        ) : currentUser?.is_superuser &&
          appMode === AppMode.PARENT_TRAINING ? null : null}

        <main
          className={`app-main ${
            appMode === AppMode.TREATMENT_GOALS
              ? domainsSidebarCollapsed
                ? "app-main--expanded"
                : ""
              : appMode === AppMode.TREATMENT_PLAN
              ? "app-main--expanded"
              : appMode === AppMode.DECISION_SUPPORT
              ? decisionSupportSidebarCollapsed
                ? "app-main--expanded"
                : ""
              : appMode === AppMode.PARENT_TRAINING
              ? "app-main--expanded"
              : ""
          }`}
        >
          {!currentUser?.is_superuser || appMode === AppMode.TREATMENT_GOALS ? (
            <GoalsManagement
              domain={domains.find((d) => d.id === selectedDomainId) || null}
              goals={domainGoals}
              onRefresh={refreshGoalsAndDomains}
              currentUser={currentUser}
              authors={authors}
            />
          ) : currentUser?.is_superuser &&
            appMode === AppMode.TREATMENT_PLAN ? (
            <TreatmentPlanWizard models={models} />
          ) : currentUser?.is_superuser &&
            appMode === AppMode.DECISION_SUPPORT ? (
            <>
              <NewSessionModal
                isOpen={showNewSessionModal}
                onClose={() => setShowNewSessionModal(false)}
                onCreateSession={handleCreateSession}
              />

              <DecisionSupportPanel
                activeSession={
                  sessions.find((s) => s.id === activeSessionId) || null
                }
                messages={sessionMessages}
                onSendMessage={handleSendMessage}
                isSending={isSendingMessage}
                isLoadingMessages={isLoadingMessages}
                streamingContent={streamingContent}
                isStreaming={isStreaming}
              />
            </>
          ) : currentUser?.is_superuser &&
            appMode === AppMode.PARENT_TRAINING ? (
            <ParentTrainingPanel />
          ) : null}
        </main>
      </div>

      <footer className="app-footer">
        <div className="app-footer__content">
          <p>
            © 2025 ANNA - Allied Network for Neurodevelopmental Advancement |
            Powered by AWS Bedrock & Claude AI
          </p>
          <div className="app-footer__links">
            <a href="#help" className="footer-link">
              Help
            </a>
            <span className="footer-separator">•</span>
            <a href="#privacy" className="footer-link">
              Privacy
            </a>
            <span className="footer-separator">•</span>
            <a href="#terms" className="footer-link">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
